// PAYMENT-CODING-1 (2026-06-01) — Legacy Payment Coding Workbench.
// Operations → Payment Coding. A three-panel financial-operations workstation:
//   LEFT   filters / work queue (facet-counted)
//   CENTER server-paginated payment grid (sortable, sticky, bulk-select)
//   RIGHT  coding workspace (allocation bars + integrity + funding + suggestions)
// Codes the ~47k historical payments. ~46k importer-coded rows are 'imported'
// (filterable provenance, not work); ~1k truly-uncoded are the default queue.
// Hard rule: when any category is set, the 10 splits MUST equal the amount
// (server-enforced 422) — partial coding silently drops report grand totals.
import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch, fmt, can } from "../api";
import { StatusPill } from "./ui";
import EntityDocuments from "./EntityDocuments";  // DC-4b: payment support docs

const CATS = [
  { k: "medical", label: "Medical" }, { k: "bill_review", label: "Bill Review" },
  { k: "transport", label: "Transport" }, { k: "maintenance", label: "Maintenance" },
  { k: "legal", label: "Legal" }, { k: "adjusting", label: "Adjusting" },
  { k: "uew", label: "UEW" }, { k: "settlement", label: "Settlement" },
  { k: "reimburse", label: "Reimburse" }, { k: "other_expense", label: "Other" },
];
const CAT_COLOR = {
  medical: "#2563eb", bill_review: "#7c3aed", transport: "#0891b2", maintenance: "#16a34a",
  legal: "#dc2626", adjusting: "#d97706", uew: "#db2777", settlement: "#0f766e",
  reimburse: "#9333ea", other_expense: "#64748b",
};
const FUNDING = ["trust", "owner", "assured", "underwriters", "other"];
const STATUS_META = {
  uncoded: { label: "Uncoded", color: "#d97706" },
  imported: { label: "Imported", color: "#64748b" },
  coded: { label: "Coded", color: "#16a34a" },
  needs_review: { label: "Needs Review", color: "#dc2626" },
};
const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const money0 = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 }));

function StatusChip({ status, small }) {
  const m = STATUS_META[status];
  return <StatusPill status={status} title={m ? m.label : status}>{m ? m.label : status}</StatusPill>;
}

// Horizontal allocation bar — categories proportional to amount; remainder red.
function AllocBar({ cats, amount }) {
  const amt = Number(amount) || 0;
  const segs = CATS.map(c => ({ c, v: Number(cats[c.k]) || 0 })).filter(s => s.v !== 0);
  const used = segs.reduce((s, x) => s + x.v, 0);
  const rem = Math.max(0, amt - used);
  return (
    <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: "#f1f5f9", border: "1px solid var(--border)" }} title={`Allocated ${money(used)} of ${money(amt)}`}>
      {segs.map(s => <div key={s.c.k} style={{ width: `${amt ? (s.v / amt) * 100 : 0}%`, background: CAT_COLOR[s.c.k] }} title={`${s.c.label} ${money(s.v)}`} />)}
      {rem > 0.005 && <div style={{ width: `${amt ? (rem / amt) * 100 : 0}%`, background: "#fecaca" }} title={`Unallocated ${money(rem)}`} />}
    </div>
  );
}

const SortTh = ({ id, label, sort, dir, onSort, style }) => (
  <th onClick={() => onSort(id)} style={{ cursor: "pointer", padding: "6px 8px", textAlign: "left", fontSize: 10.5, fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f8fafc", borderBottom: "1px solid var(--border)", ...style }}>
    {label}{sort === id ? (dir === "asc" ? " ▲" : " ▼") : ""}
  </th>
);

// ─── RIGHT: coding workspace for one payment ─────────────────────────────────
function CodingWorkspace({ payment, onSaved }) {
  const blank = useCallback(() => {
    const f = { funding_source: payment.funding_source || "", coding_status: payment.coding_status || "uncoded",
      coding_notes: payment.coding_notes || "", coding_reviewer_id: payment.coding_reviewer_id || "" };
    CATS.forEach(c => { f[c.k] = payment[`category_${c.k}`] != null ? Number(payment[`category_${c.k}`]) : 0; });
    return f;
  }, [payment]);
  const [form, setForm] = useState(blank);
  const [users, setUsers] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setForm(blank()); setMsg(null); }, [payment.id, blank]);
  useEffect(() => { apiFetch("/users/assignable").then(d => setUsers(d || [])).catch(() => {}); }, []);
  useEffect(() => { apiFetch(`/payments/coding/suggest?payment_id=${payment.id}`).then(setSuggest).catch(() => setSuggest(null)); }, [payment.id]);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const amt = Number(payment.amount) || 0;
  const allocated = CATS.reduce((s, c) => s + (parseFloat(form[c.k]) || 0), 0);
  const diff = Math.round((allocated - amt) * 100) / 100;
  const anyCat = CATS.some(c => (parseFloat(form[c.k]) || 0) !== 0);
  const mismatch = anyCat && diff !== 0;

  const applySuggestion = () => {
    if (!suggest?.suggested_category) return;
    const next = {}; CATS.forEach(c => { next[c.k] = 0; }); next[suggest.suggested_category] = amt;
    setForm(f => ({ ...f, ...next, coding_status: "coded" }));
  };
  const save = async () => {
    if (mismatch) { setMsg("Category splits must equal the amount."); return; }
    setBusy(true); setMsg(null);
    try {
      const categories = {}; CATS.forEach(c => { categories[c.k] = parseFloat(form[c.k]) || 0; });
      const body = { categories, funding_source: form.funding_source || null,
        coding_status: form.coding_status, coding_notes: form.coding_notes || null,
        coding_reviewer_id: form.coding_reviewer_id ? parseInt(form.coding_reviewer_id) : null };
      await apiFetch(`/payments/${payment.id}/coding`, { method: "PATCH", body });
      setMsg("Saved."); onSaved && onSaved();
    } catch (e) { setMsg(String(e.message || e).replace(/^"|"$/g, "").slice(0, 200)); } finally { setBusy(false); }
  };

  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <StatusChip status={payment.coding_status} />
        {payment.claim_number && <span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{payment.claim_number}</span>}
        <span style={{ marginLeft: "auto", fontWeight: 800, color: "var(--navy)", fontSize: 14 }}>{money(amt)}</span>
      </div>
      <table style={{ width: "100%", fontSize: 11.5, marginBottom: 10 }}><tbody>
        <Row label="Payee" v={payment.payee} />
        <Row label="Date" v={payment.payment_date && fmt.date(payment.payment_date)} />
        <Row label="Check #" v={payment.cheque_number_raw} />
        <Row label="Source" v={payment.category_source} />
      </tbody></table>

      {/* suggestion */}
      {suggest?.suggested_category && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: "var(--text2)" }}>Suggest:</span>
          <span style={{ fontWeight: 700, color: CAT_COLOR[suggest.suggested_category] }}>{CATS.find(c => c.k === suggest.suggested_category)?.label}</span>
          <span style={{ fontSize: 10, color: "var(--text3)" }}>({suggest.basis === "vendor_history" ? `vendor: ${suggest.vendor_coded_count}/${suggest.vendor_payment_count} coded` : "keyword"})</span>
          <button className="btn btn-sm btn-blue" style={{ marginLeft: "auto" }} onClick={applySuggestion}>Apply to {money0(amt)}</button>
        </div>
      )}

      {/* allocation */}
      <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Category allocation</div>
      <div style={{ marginBottom: 6 }}><AllocBar cats={form} amount={amt} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 10px", marginBottom: 6 }}>
        {CATS.map(c => (
          <div key={c.k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: CAT_COLOR[c.k], flexShrink: 0 }} />
            <label style={{ fontSize: 10.5, color: "var(--text2)", width: 72, flexShrink: 0 }}>{c.label}</label>
            <input type="number" step="0.01" value={form[c.k] || ""} onChange={e => up(c.k, e.target.value)} style={{ ...inp, padding: "3px 5px", fontSize: 11 }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, padding: "4px 0", borderTop: "1px solid var(--border)", marginBottom: 8 }}>
        <span style={{ color: "var(--text2)" }}>Allocated <strong>{money(allocated)}</strong> of {money(amt)}</span>
        <span style={{ fontWeight: 700, color: mismatch ? "#dc2626" : diff === 0 && anyCat ? "#16a34a" : "var(--text3)" }}>
          Diff {money(diff)} {mismatch ? "✗" : anyCat ? "✓" : ""}
        </span>
      </div>

      {/* funding / status / reviewer */}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Funding source</label>
          <select value={form.funding_source} onChange={e => up("funding_source", e.target.value)} style={inp}>
            <option value="">—</option>{FUNDING.map(f => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={lbl}>Coding status</label>
          <select value={form.coding_status} onChange={e => up("coding_status", e.target.value)} style={inp}>
            {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
      </div>
      <label style={{ ...lbl, marginTop: 6 }}>Reviewer</label>
      <select value={form.coding_reviewer_id} onChange={e => up("coding_reviewer_id", e.target.value)} style={inp}>
        <option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>
      <label style={{ ...lbl, marginTop: 6 }}>Coding notes</label>
      <textarea value={form.coding_notes} onChange={e => up("coding_notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} />
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: /saved/i.test(msg) ? "#16a34a" : "#dc2626" }}>{msg}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <button className="btn btn-blue btn-sm" disabled={busy || mismatch} onClick={save}>{busy ? "Saving…" : "Save coding"}</button>
      </div>
      {/* DC-4b (2026-06-17): payment support docs (check copies, remittance backup). */}
      <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        <EntityDocuments entityType="payment" entityId={payment.id} canEdit={can("code_payments")} defaultPhi={false} title="Payment support" />
      </div>
    </div>
  );
}
const Row = ({ label, v }) => v ? <tr><td style={{ color: "var(--text3)", padding: "2px 8px 2px 0", whiteSpace: "nowrap" }}>{label}</td><td style={{ color: "var(--text1,#1a2438)", padding: "2px 0" }}>{v}</td></tr> : null;

// ─── BULK action bar ─────────────────────────────────────────────────────────
function BulkBar({ ids, onDone, onClear }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async (action, value) => {
    if (!value && value !== 0) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiFetch("/payments/coding/bulk", { method: "POST", body: { payment_ids: [...ids], action, value } });
      setMsg(`${r.updated} updated${r.skipped?.length ? `, ${r.skipped.length} skipped` : ""}.`);
      onDone && onDone();
    } catch (e) { setMsg(String(e.message || e).slice(0, 160)); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--navy)", color: "#fff", padding: "8px 14px", borderRadius: 8, marginBottom: 8, flexWrap: "wrap" }}>
      <strong>{ids.size} selected</strong>
      <BulkPick label="Set category" disabled={busy} options={CATS.map(c => [c.k, c.label])} onPick={v => run("set_single_category", v)} hint="(whole amount → one category)" />
      <BulkPick label="Set funding" disabled={busy} options={FUNDING.map(f => [f, f[0].toUpperCase() + f.slice(1)])} onPick={v => run("set_funding", v)} />
      <BulkPick label="Set status" disabled={busy} options={Object.keys(STATUS_META).map(s => [s, STATUS_META[s].label])} onPick={v => run("set_status", v)} />
      {msg && <span style={{ fontSize: 11.5 }}>{msg}</span>}
      <button className="btn btn-sm" style={{ marginLeft: "auto", background: "rgba(255,255,255,0.15)", color: "#fff", border: "none" }} onClick={onClear}>Clear</button>
    </div>
  );
}
function BulkPick({ label, options, onPick, disabled, hint }) {
  return (
    <select disabled={disabled} defaultValue="" onChange={e => { if (e.target.value) { onPick(e.target.value); e.target.value = ""; } }}
      title={hint} style={{ fontSize: 11.5, padding: "3px 6px", borderRadius: 5, border: "none" }}>
      <option value="">{label}…</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

// ─── Filters (left panel) ────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { k: "work", label: "Work queue", facet: f => (f.uncoded || 0) + (f.needs_review || 0) },
  { k: "uncoded", label: "Uncoded", facet: f => f.uncoded },
  { k: "needs_review", label: "Needs Review", facet: f => f.needs_review },
  { k: "imported", label: "Imported", facet: f => f.imported },
  { k: "coded", label: "Coded", facet: f => f.coded },
  { k: "all", label: "All", facet: f => f.total },
];

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function PaymentCoding({ onNavigateToClaim, onWorkChanged }) {
  const [facets, setFacets] = useState({});
  const [filters, setFilters] = useState({ status: "work", missing_funding: false, reimbursement: false, mismatch: false, funding: "", claim_number: "", payee: "" });
  const [sort, setSort] = useState("payment_date");
  const [dir, setDir] = useState("desc");
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [active, setActive] = useState(null);
  const debRef = useRef(null);

  const loadFacets = useCallback(() => { apiFetch("/payments/coding/facets").then(setFacets).catch(() => {}); }, []);
  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ status: filters.status, sort, dir, limit: String(limit), offset: String(offset) });
    if (filters.funding) q.set("funding", filters.funding);
    if (filters.missing_funding) q.set("missing_funding", "true");
    if (filters.reimbursement) q.set("reimbursement", "true");
    if (filters.mismatch) q.set("mismatch", "true");
    if (filters.claim_number) q.set("claim_number", filters.claim_number);
    if (filters.payee) q.set("payee", filters.payee);
    apiFetch(`/payments/coding?${q.toString()}`).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [filters, sort, dir, offset]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  // debounce text filters; immediate for the rest
  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(load, 220);
    return () => clearTimeout(debRef.current);
  }, [load]);

  const setFilter = (patch) => { setFilters(f => ({ ...f, ...patch })); setOffset(0); setSel(new Set()); };
  const onSort = (id) => { if (sort === id) setDir(d => d === "asc" ? "desc" : "asc"); else { setSort(id); setDir("desc"); } setOffset(0); };
  const afterWrite = () => { load(); loadFacets(); setSel(new Set()); onWorkChanged && onWorkChanged(); };
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pageIds = data.rows.map(r => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every(id => sel.has(id));
  const toggleAll = () => setSel(s => { const n = new Set(s); allOnPage ? pageIds.forEach(id => n.delete(id)) : pageIds.forEach(id => n.add(id)); return n; });

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      {/* LEFT filters */}
      <div style={{ width: 210, flexShrink: 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Queue</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 12 }}>
          {STATUS_FILTERS.map(s => {
            const n = s.facet(facets); const on = filters.status === s.k;
            return (
              <button key={s.k} onClick={() => setFilter({ status: s.k })}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: on ? 700 : 500,
                  border: `1px solid ${on ? "var(--navy)" : "var(--border)"}`, background: on ? "var(--navy)" : "#fff", color: on ? "#fff" : "var(--text1,#1a2438)" }}>
                <span>{s.label}</span><span style={{ fontSize: 11, opacity: 0.85 }}>{n != null ? n.toLocaleString() : "—"}</span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Flags</div>
        {[["missing_funding", "Missing funding", facets.missing_funding], ["reimbursement", "Reimbursement", facets.reimbursement], ["mismatch", "Split mismatch", facets.split_mismatch]].map(([k, label, n]) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 0", cursor: "pointer", color: "var(--text1,#1a2438)" }}>
            <input type="checkbox" checked={filters[k]} onChange={e => setFilter({ [k]: e.target.checked })} />
            {label}<span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--text3)" }}>{n != null ? n.toLocaleString() : ""}</span>
          </label>
        ))}
        <div style={{ marginTop: 12 }}>
          <label style={lbl}>Funding</label>
          <select value={filters.funding} onChange={e => setFilter({ funding: e.target.value })} style={inp}>
            <option value="">Any</option>{FUNDING.map(f => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}
          </select>
          <label style={{ ...lbl, marginTop: 8 }}>Claim #</label>
          <input value={filters.claim_number} onChange={e => setFilter({ claim_number: e.target.value })} placeholder="e.g. 26-00" style={inp} />
          <label style={{ ...lbl, marginTop: 8 }}>Payee</label>
          <input value={filters.payee} onChange={e => setFilter({ payee: e.target.value })} placeholder="vendor…" style={inp} />
        </div>
      </div>

      {/* CENTER grid */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {sel.size > 0 && <BulkBar ids={sel} onDone={afterWrite} onClear={() => setSel(new Set())} />}
        <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "auto", maxHeight: "74vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ position: "sticky", top: 0, background: "#f8fafc", borderBottom: "1px solid var(--border)", padding: "6px 8px", width: 28 }}>
                  <input type="checkbox" checked={allOnPage} onChange={toggleAll} title="Select page" />
                </th>
                <SortTh id="payment_date" label="Date" sort={sort} dir={dir} onSort={onSort} />
                <SortTh id="claim_number" label="Claim" sort={sort} dir={dir} onSort={onSort} />
                <SortTh id="payee" label="Payee" sort={sort} dir={dir} onSort={onSort} />
                <SortTh id="amount" label="Amount" sort={sort} dir={dir} onSort={onSort} style={{ textAlign: "right" }} />
                <th style={thS}>Funding</th>
                <th style={thS}>Allocation</th>
                <SortTh id="coding_status" label="Status" sort={sort} dir={dir} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {loading && data.rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "var(--text3)" }}>Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "var(--text3)" }}>No payments match.</td></tr>
              ) : data.rows.map(r => {
                const on = active?.id === r.id; const fl = r.flags || {};
                return (
                  <tr key={r.id} onClick={() => setActive(r)} style={{ cursor: "pointer", background: on ? "#eff6ff" : sel.has(r.id) ? "#f0f9ff" : "#fff", borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "5px 8px" }} onClick={e => { e.stopPropagation(); toggle(r.id); }}><input type="checkbox" checked={sel.has(r.id)} onChange={() => {}} /></td>
                    <td style={td}>{r.payment_date ? fmt.date(r.payment_date) : "—"}</td>
                    <td style={td}><span className="mono" style={{ color: "var(--blue)", fontSize: 11 }}>{r.claim_number || "—"}</span></td>
                    <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fl.split_mismatch && <span title="splits don't match amount" style={{ color: "#dc2626" }}>⚠ </span>}
                      {fl.is_uncoded && <span title="uncoded" style={{ color: "#d97706" }}>● </span>}
                      {r.payee || "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{money(r.amount)}</td>
                    <td style={td}>{r.funding_source ? r.funding_source[0].toUpperCase() + r.funding_source.slice(1) : <span style={{ color: "#d97706" }} title="missing funding">⚠ none</span>}</td>
                    <td style={{ ...td, width: 120 }}><AllocBar cats={Object.fromEntries(CATS.map(c => [c.k, r[`category_${c.k}`]]))} amount={r.amount} /></td>
                    <td style={td}><StatusChip status={r.coding_status} small /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* pagination */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, fontSize: 12, color: "var(--text2)" }}>
          <span>{data.total ? `${offset + 1}–${Math.min(offset + limit, data.total)} of ${data.total.toLocaleString()}` : "0"}</span>
          <button className="btn btn-sm btn-outline" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))}>← Prev</button>
          <button className="btn btn-sm btn-outline" disabled={offset + limit >= data.total} onClick={() => setOffset(o => o + limit)}>Next →</button>
          {loading && <span style={{ color: "var(--text3)" }}>…</span>}
        </div>
      </div>

      {/* RIGHT coding workspace */}
      <div style={{ width: 360, flexShrink: 0 }}>
        <div className="card" style={{ padding: 14 }}>
          {active ? <CodingWorkspace payment={active} onSaved={afterWrite} />
            : <div style={{ padding: 30, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Select a payment to code.<br />Or select rows (checkboxes) for bulk actions.</div>}
        </div>
      </div>
    </div>
  );
}

const lbl = { display: "block", fontSize: 10.5, fontWeight: 600, color: "var(--text3)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.3 };
const inp = { width: "100%", fontSize: 12, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, fontFamily: "inherit", boxSizing: "border-box", background: "#fff" };
const td = { padding: "5px 8px", color: "var(--text1,#1a2438)" };
const thS = { padding: "6px 8px", textAlign: "left", fontSize: 10.5, fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: 0.3, position: "sticky", top: 0, background: "#f8fafc", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
